// biome-ignore-all lint/security/noSecrets: Sanity query names are telemetry labels, not secrets.
import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { queryClient } from "~/entry.client";
import { normalizeSeriesOwnershipType } from "~/lib/seriesRoute.utils";
import { getSongTypeCategoryConfig, isSongTypeCategory, type SongTypeCategory } from "~/lib/songTypes";
import type {
  GetAllDevotionalSeriesForDashboardResult,
  GetAllEpisodesBySeriesSlugQueryResult,
  GetAllEpisodesMinimalBySeriesSlugQueryResult,
  GetAllWorshipDevotionalsForBibleBrowseQueryResult,
  GetAllWorshipDevotionalsMinimalQueryResult,
  GetAllWorshipDevotionalsQueryResult,
  GetArrayDevotionalsByIdsQueryResult,
  GetDevotionalsByBibleBookQueryResult,
  GetEpisodeBySlugAndSeriesSlugQueryResult,
  GetFirstEpisodeFromSeriesResult,
  GetLatestAvailableEpisodeFromSerieResult,
  GetSeriesBySlugQueryResult,
  GetSingleDevotionalByIdResult,
  GetWorshipDevotionalByDateResult,
} from "~/sanity/sanity.types";
import {
  fetchEpisodeBySlugsFromCachedSanity,
  type GetSongTypeDevotionalsQueryResult,
  getAllDevotionalSeriesForDashboard,
  getAllEpisodesBySeriesSlugQuery,
  getAllEpisodesMinimalBySeriesSlugQuery,
  getAllWorshipDevotionalsForBibleBrowseQuery,
  getAllWorshipDevotionalsMinimalQuery,
  getAllWorshipDevotionalsQuery,
  getArrayDevotionalsByIdsQuery,
  getDevotionalsByBibleBookQuery,
  getEpisodeBySlugAndSeriesSlugQuery,
  getFirstEpisodeFromSeries,
  getLatestAvailableEpisodeFromSerie,
  getSeriesBySlugQuery,
  getSingleDevotionalById,
  getSongTypeDevotionalsQuery,
  getWorshipDevotionalByDate,
  sanityClient,
  sanityOriginOnlyClient,
} from "~/services/sanity";

dayjs.extend(utc);
dayjs.extend(timezone);

const FIVE_MINUTES = 1000 * 60 * 5;

export const CONTENT_QUERY_KEY = {
  all: ["content"],
  byDate: (date: string) => [...CONTENT_QUERY_KEY.all, "date", date],
  latestAvailable: (timezone: string) => [...CONTENT_QUERY_KEY.all, "latestAvailable", timezone],
  byIds: (ids: Array<string>) => [...CONTENT_QUERY_KEY.all, "ids", ids.sort().join(",")],
  allSeries: () => [...CONTENT_QUERY_KEY.all, "allSeries"],
  devotionalById: (id: string) => [...CONTENT_QUERY_KEY.all, "devotional", id],
  bibleBooks: () => [...CONTENT_QUERY_KEY.all, "bibleBooks"],
  bibleBook: (book: string) => [...CONTENT_QUERY_KEY.all, "bibleBook", book],
  seriesBySlug: (slug: string, ownershipType: string) => [...CONTENT_QUERY_KEY.all, "series", ownershipType, slug],
  episodeBySlugs: (seriesSlug: string, episodeSlug: string, ownershipType: string) => [
    ...CONTENT_QUERY_KEY.all,
    "episode",
    ownershipType,
    seriesSlug,
    episodeSlug,
  ],
  latestEpisode: (seriesId: string) => [...CONTENT_QUERY_KEY.all, "latestEpisode", seriesId],
  firstEpisode: (seriesId: string) => [...CONTENT_QUERY_KEY.all, "firstEpisode", seriesId],
  episodesBySeries: (seriesSlug: string, ownershipType: string) => [
    ...CONTENT_QUERY_KEY.all,
    "episodes",
    ownershipType,
    seriesSlug,
  ],
  episodesMinimal: (seriesSlug: string, ownershipType: string) => [
    ...CONTENT_QUERY_KEY.all,
    "episodesMinimal",
    ownershipType,
    seriesSlug,
  ],
  songTypeDevotionals: (category: SongTypeCategory) => [...CONTENT_QUERY_KEY.all, "songTypeDevotionals", category],
} as const;

export const contentByDateQueryOptions = (date: string) =>
  ({
    queryKey: CONTENT_QUERY_KEY.byDate(date),
    queryFn: async () => {
      try {
        return await sanityClient.fetch<GetWorshipDevotionalByDateResult>(
          getWorshipDevotionalByDate,
          {
            releaseDate: date,
          },
          { queryName: "getWorshipDevotionalByDate" },
        );
      } catch (error) {
        console.error("Error fetching content by date:", error);
        return null;
      }
    },
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
  }) satisfies UseQueryOptions<GetWorshipDevotionalByDateResult | null, Error>;

export function useContentByDate(date: string) {
  return useQuery(contentByDateQueryOptions(date));
}

export function ensureContentByDate(date: string) {
  return queryClient.ensureQueryData(contentByDateQueryOptions(date));
}

export const latestAvailableContentQueryOptions = (userTimezone: string) =>
  ({
    queryKey: CONTENT_QUERY_KEY.latestAvailable(userTimezone),
    queryFn: async () => {
      try {
        const todayInUsersTz = dayjs().tz(userTimezone).format("YYYY-MM-DD");

        const content = await sanityClient.fetch<GetAllWorshipDevotionalsQueryResult>(
          getAllWorshipDevotionalsQuery,
          undefined,
          { queryName: "getAllWorshipDevotionalsQuery" },
        );

        const latestContent = content
          .filter((c) => !dayjs(c.releaseDate).tz(userTimezone).isAfter(todayInUsersTz))
          .sort((a, b) => {
            if (new Date(a.releaseDate || 0) > new Date(b.releaseDate || 0)) {
              return -1;
            }
            if (new Date(a.releaseDate || 0) < new Date(b.releaseDate || 0)) {
              return 1;
            }
            return 0;
          });

        console.info(
          `Latest available content was ${latestContent[0]?.releaseDate}. The date in user's timezone was ${todayInUsersTz}`,
        );

        if (!latestContent.length) {
          console.warn(`No available content found. The date in user's timezone was ${todayInUsersTz}`);
          return null;
        }

        return latestContent[0];
      } catch (error) {
        console.error("Error fetching latest available content:", error);
        return null;
      }
    },
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
  }) satisfies UseQueryOptions<GetAllWorshipDevotionalsQueryResult[number] | null, Error>;

export function useLatestAvailableContent(timezone: string) {
  return useQuery(latestAvailableContentQueryOptions(timezone));
}

export function ensureLatestAvailableContent(timezone: string) {
  return queryClient.ensureQueryData(latestAvailableContentQueryOptions(timezone));
}

export const contentByIdsQueryOptions = (ids?: Array<string>) =>
  ({
    queryKey: CONTENT_QUERY_KEY.byIds(ids ?? []),
    queryFn: async () => {
      try {
        if (ids && ids.length > 0) {
          return await sanityOriginOnlyClient.fetch<GetArrayDevotionalsByIdsQueryResult>(
            getArrayDevotionalsByIdsQuery,
            {
              ids,
            },
          );
        }

        return await sanityClient.fetch<GetAllWorshipDevotionalsMinimalQueryResult>(
          getAllWorshipDevotionalsMinimalQuery,
          undefined,
          { queryName: "getAllWorshipDevotionalsMinimalQuery" },
        );
      } catch (error) {
        console.error("Error fetching content by IDs:", error);
        return [];
      }
    },
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
  }) satisfies UseQueryOptions<GetArrayDevotionalsByIdsQueryResult | GetAllWorshipDevotionalsMinimalQueryResult, Error>;

export function useContentByIds(ids?: Array<string>) {
  return useQuery(contentByIdsQueryOptions(ids));
}

export function ensureContentByIds(ids?: Array<string>) {
  return queryClient.ensureQueryData(contentByIdsQueryOptions(ids));
}

export const allSeriesQueryOptions = {
  queryKey: CONTENT_QUERY_KEY.allSeries(),
  queryFn: async () => {
    try {
      return await sanityClient.fetch<GetAllDevotionalSeriesForDashboardResult>(
        getAllDevotionalSeriesForDashboard,
        undefined,
        { queryName: "getAllDevotionalSeriesForDashboard" },
      );
    } catch (error) {
      console.error("Error fetching all series:", error);
      return [];
    }
  },
  staleTime: FIVE_MINUTES,
  refetchOnMount: false,
} satisfies UseQueryOptions<GetAllDevotionalSeriesForDashboardResult, Error>;

export function useAllSeries() {
  return useQuery(allSeriesQueryOptions);
}

export function ensureAllSeries() {
  return queryClient.ensureQueryData(allSeriesQueryOptions);
}

export const devotionalByIdQueryOptions = (id?: string) =>
  ({
    queryKey: CONTENT_QUERY_KEY.devotionalById(id ?? ""),
    queryFn: async () => {
      try {
        if (!id) {
          console.warn("Devotional ID is required");
          return null;
        }

        return await sanityClient.fetch<GetSingleDevotionalByIdResult>(
          getSingleDevotionalById,
          {
            id,
          },
          { queryName: "getSingleDevotionalById" },
        );
      } catch (error) {
        console.error("Error fetching devotional by ID:", error);
        return null;
      }
    },
    enabled: !!id,
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
  }) satisfies UseQueryOptions<GetSingleDevotionalByIdResult | null, Error>;

export function useDevotionalById(id?: string) {
  return useQuery(devotionalByIdQueryOptions(id));
}

export function ensureDevotionalById(id: string) {
  return queryClient.ensureQueryData(devotionalByIdQueryOptions(id));
}

export const bibleBooksQueryOptions = {
  queryKey: CONTENT_QUERY_KEY.bibleBooks(),
  queryFn: async () => {
    try {
      return await sanityClient.fetch<GetAllWorshipDevotionalsForBibleBrowseQueryResult>(
        getAllWorshipDevotionalsForBibleBrowseQuery,
        undefined,
        { queryName: "getAllWorshipDevotionalsForBibleBrowseQuery" },
      );
    } catch (error) {
      console.error("Error fetching Bible books browse data:", error);
      return [];
    }
  },
  staleTime: FIVE_MINUTES,
  refetchOnMount: false,
} satisfies UseQueryOptions<GetAllWorshipDevotionalsForBibleBrowseQueryResult, Error>;

export function useBibleBooks() {
  return useQuery(bibleBooksQueryOptions);
}

export function ensureBibleBooks() {
  return queryClient.ensureQueryData(bibleBooksQueryOptions);
}

export const bibleBookQueryOptions = (book?: string) =>
  ({
    queryKey: CONTENT_QUERY_KEY.bibleBook(book ?? ""),
    queryFn: async () => {
      try {
        if (!book) {
          console.warn("Bible book is required");
          return [];
        }

        return await sanityClient.fetch<GetDevotionalsByBibleBookQueryResult>(
          getDevotionalsByBibleBookQuery,
          {
            book,
          },
          { queryName: "getDevotionalsByBibleBookQuery" },
        );
      } catch (error) {
        console.error("Error fetching Bible book detail data:", error);
        return [];
      }
    },
    enabled: !!book,
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
  }) satisfies UseQueryOptions<GetDevotionalsByBibleBookQueryResult, Error>;

export function useBibleBook(book?: string) {
  return useQuery(bibleBookQueryOptions(book));
}

export function ensureBibleBook(book: string) {
  return queryClient.ensureQueryData(bibleBookQueryOptions(book));
}

export const seriesBySlugQueryOptions = (slug?: string, ownershipType?: string | null) =>
  ({
    queryKey: CONTENT_QUERY_KEY.seriesBySlug(slug ?? "", normalizeSeriesOwnershipType(ownershipType)),
    queryFn: async () => {
      try {
        if (!slug) {
          console.warn("Series slug is required");
          return null;
        }

        return await sanityClient.fetch<GetSeriesBySlugQueryResult>(
          getSeriesBySlugQuery,
          {
            seriesSlug: slug,
            ownershipType: normalizeSeriesOwnershipType(ownershipType),
          },
          { queryName: "getSeriesBySlugQuery" },
        );
      } catch (error) {
        console.error("Error fetching series by slug:", error);
        return null;
      }
    },
    enabled: !!slug,
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
  }) satisfies UseQueryOptions<GetSeriesBySlugQueryResult | null, Error>;

export function useSeriesBySlug(slug?: string, ownershipType?: string | null) {
  return useQuery(seriesBySlugQueryOptions(slug, ownershipType));
}

export function ensureSeriesBySlug(slug: string, ownershipType?: string | null) {
  return queryClient.ensureQueryData(seriesBySlugQueryOptions(slug, ownershipType));
}

export const episodeBySlugsQueryOptions = (seriesSlug: string, episodeSlug: string, ownershipType?: string | null) =>
  ({
    queryKey: CONTENT_QUERY_KEY.episodeBySlugs(seriesSlug, episodeSlug, normalizeSeriesOwnershipType(ownershipType)),
    queryFn: async () => {
      if (!seriesSlug || !episodeSlug) {
        console.warn("Series slug and episode slug are required");
        return null;
      }

      return fetchEpisodeBySlugsFromCachedSanity({
        seriesSlug,
        episodeSlug,
        ownershipType: normalizeSeriesOwnershipType(ownershipType),
      });
    },
    enabled: !!seriesSlug && !!episodeSlug,
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
  }) satisfies UseQueryOptions<GetEpisodeBySlugAndSeriesSlugQueryResult | null, Error>;

export function useEpisodeBySlugs(seriesSlug: string, episodeSlug: string, ownershipType?: string | null) {
  return useQuery(episodeBySlugsQueryOptions(seriesSlug, episodeSlug, ownershipType));
}

export function ensureEpisodeBySlugs(seriesSlug: string, episodeSlug: string, ownershipType?: string | null) {
  return queryClient.ensureQueryData(episodeBySlugsQueryOptions(seriesSlug, episodeSlug, ownershipType));
}

export async function fetchEpisodeBySlugsFromOrigin(
  seriesSlug: string,
  episodeSlug: string,
  ownershipType?: string | null,
) {
  if (!seriesSlug || !episodeSlug) {
    console.warn("Series slug and episode slug are required");
    return null;
  }

  try {
    return await sanityOriginOnlyClient.fetch<GetEpisodeBySlugAndSeriesSlugQueryResult>(
      getEpisodeBySlugAndSeriesSlugQuery,
      {
        seriesSlug,
        episodeSlug,
        ownershipType: normalizeSeriesOwnershipType(ownershipType),
      },
      { queryName: "getEpisodeBySlugAndSeriesSlugQuery", perspective: "published" },
    );
  } catch (error) {
    console.error("Error fetching episode by slugs from Sanity origin:", error);
    return null;
  }
}

export const latestEpisodeQueryOptions = (seriesId?: string) =>
  ({
    queryKey: CONTENT_QUERY_KEY.latestEpisode(seriesId ?? ""),
    queryFn: async () => {
      try {
        if (!seriesId) {
          console.warn("Series ID is required");
          return null;
        }

        const today = dayjs().format("YYYY-MM-DD");

        return await sanityClient.fetch<GetLatestAvailableEpisodeFromSerieResult>(
          getLatestAvailableEpisodeFromSerie,
          {
            seriesId,
            today,
          },
          { queryName: "getLatestAvailableEpisodeFromSerie" },
        );
      } catch (error) {
        console.error("Error fetching latest episode:", error);
        return null;
      }
    },
    enabled: !!seriesId,
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
  }) satisfies UseQueryOptions<GetLatestAvailableEpisodeFromSerieResult | null, Error>;

export function useLatestEpisode(seriesId?: string) {
  return useQuery(latestEpisodeQueryOptions(seriesId));
}

export function ensureLatestEpisode(seriesId: string) {
  return queryClient.ensureQueryData(latestEpisodeQueryOptions(seriesId));
}

export const firstEpisodeQueryOptions = (seriesId?: string) =>
  ({
    queryKey: CONTENT_QUERY_KEY.firstEpisode(seriesId ?? ""),
    queryFn: async () => {
      try {
        if (!seriesId) {
          console.warn("Series ID is required");
          return null;
        }

        return await sanityClient.fetch<GetFirstEpisodeFromSeriesResult>(
          getFirstEpisodeFromSeries,
          {
            seriesId,
          },
          { queryName: "getFirstEpisodeFromSeries" },
        );
      } catch (error) {
        console.error("Error fetching first episode:", error);
        return null;
      }
    },
    enabled: !!seriesId,
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
  }) satisfies UseQueryOptions<GetFirstEpisodeFromSeriesResult | null, Error>;

export function useFirstEpisode(seriesId?: string) {
  return useQuery(firstEpisodeQueryOptions(seriesId));
}

export function ensureFirstEpisode(seriesId: string) {
  return queryClient.ensureQueryData(firstEpisodeQueryOptions(seriesId));
}

export const episodesBySeriesQueryOptions = (seriesSlug?: string, userDate?: string, ownershipType?: string | null) =>
  ({
    queryKey: CONTENT_QUERY_KEY.episodesBySeries(seriesSlug ?? "", normalizeSeriesOwnershipType(ownershipType)),
    queryFn: async () => {
      try {
        if (!seriesSlug) {
          console.warn("Series slug is required");
          return [];
        }

        const dateToUse = userDate ?? dayjs().format("YYYY-MM-DD");
        const parsedDate = new Date(dateToUse);
        if (Number.isNaN(parsedDate.getTime())) {
          console.warn(`Invalid date format: ${dateToUse}`);
          return [];
        }
        const normalizedDate = parsedDate.toISOString().split("T")[0];

        return await sanityClient.fetch<GetAllEpisodesBySeriesSlugQueryResult>(
          getAllEpisodesBySeriesSlugQuery,
          {
            seriesSlug,
            today: normalizedDate,
            ownershipType: normalizeSeriesOwnershipType(ownershipType),
            previewKey: null,
          },
          { queryName: "getAllEpisodesBySeriesSlugQuery" },
        );
      } catch (error) {
        console.error("Error fetching episodes by series:", error);
        return [];
      }
    },
    enabled: !!seriesSlug,
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
  }) satisfies UseQueryOptions<GetAllEpisodesBySeriesSlugQueryResult, Error>;

export function useEpisodesBySeries(seriesSlug?: string, userDate?: string, ownershipType?: string | null) {
  return useQuery(episodesBySeriesQueryOptions(seriesSlug, userDate, ownershipType));
}

export function ensureEpisodesBySeries(seriesSlug: string, userDate?: string, ownershipType?: string | null) {
  return queryClient.ensureQueryData(episodesBySeriesQueryOptions(seriesSlug, userDate, ownershipType));
}

export const episodesMinimalQueryOptions = (seriesSlug?: string | null, ownershipType?: string | null) =>
  ({
    queryKey: CONTENT_QUERY_KEY.episodesMinimal(seriesSlug ?? "", normalizeSeriesOwnershipType(ownershipType)),
    queryFn: async () => {
      try {
        if (!seriesSlug) {
          console.warn("Series slug is required");
          return [];
        }

        const today = dayjs().format("YYYY-MM-DD");

        return await sanityClient.fetch<GetAllEpisodesMinimalBySeriesSlugQueryResult>(
          getAllEpisodesMinimalBySeriesSlugQuery,
          {
            seriesSlug,
            today,
            ownershipType: normalizeSeriesOwnershipType(ownershipType),
          },
          { queryName: "getAllEpisodesMinimalBySeriesSlugQuery" },
        );
      } catch (error) {
        console.error("Error fetching minimal episodes:", error);
        return [];
      }
    },
    enabled: !!seriesSlug,
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
  }) satisfies UseQueryOptions<GetAllEpisodesMinimalBySeriesSlugQueryResult, Error>;

export function useEpisodesMinimal(seriesSlug?: string | null, ownershipType?: string | null) {
  return useQuery(episodesMinimalQueryOptions(seriesSlug, ownershipType));
}

export function ensureEpisodesMinimal(seriesSlug: string, ownershipType?: string | null) {
  return queryClient.ensureQueryData(episodesMinimalQueryOptions(seriesSlug, ownershipType));
}

export const songTypeDevotionalsQueryOptions = (category?: SongTypeCategory | null) =>
  ({
    queryKey: CONTENT_QUERY_KEY.songTypeDevotionals((category ?? "psalms") as SongTypeCategory),
    queryFn: async () => {
      if (!category || !isSongTypeCategory(category)) {
        throw new Error(`Invalid song type category: ${category ?? "unknown"}`);
      }

      const config = getSongTypeCategoryConfig(category);

      return await sanityClient.fetch<GetSongTypeDevotionalsQueryResult>(
        getSongTypeDevotionalsQuery,
        {
          category,
          songType: config.sanityParam,
        },
        { queryName: "getSongTypeDevotionalsQuery" },
      );
    },
    enabled: Boolean(category),
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
  }) satisfies UseQueryOptions<GetSongTypeDevotionalsQueryResult, Error>;

export function useSongTypeDevotionals(category?: SongTypeCategory | null) {
  return useQuery(songTypeDevotionalsQueryOptions(category));
}

export function ensureSongTypeDevotionals(category: SongTypeCategory) {
  return queryClient.ensureQueryData(songTypeDevotionalsQueryOptions(category));
}

export function invalidateContentQueries() {
  return queryClient.invalidateQueries({ queryKey: CONTENT_QUERY_KEY.all });
}
